function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-05-27 15:23:24 +0200 (Wed, 27 May 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163322 $
// Purpose     : a view is a class/tab node in templateconfig.xml
//               a view chooses it's layout which will distribute the parts
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Message, Loader, Modal, Header, Button, Icon } from 'semantic-ui-react';
import { Title } from 'react-head';
// ---Framework imports
import { mapItems, items, has, find, entries, get, each, isArray, isNil } from 'ldash';
import connect from 'uix/connect';
import { Actions, Sel } from 'uix/registries';
import { Parts } from 'uix/registry';
import { StandalonePart } from 'uix/parts';
import { ucFirst } from 'uix/utils';
import NotificationManager from 'uix/notify/component';
import { PortalTarget } from 'uix/components/portals';
import { GlobalKeyboardShortcuts } from 'uix/components/keyboardShortcuts';
import { ViewLoader } from 'uix/components/viewLoader';
import { useEffect, useState, useActions, useSelector } from 'uix/hooks';
import log from 'uix/logging';
import { applyTheme } from 'uix/styles/variables';
import { setCustomVital } from 'uix/utils/vitals';
// ---Local imports
import { DomainStyles } from './css';
import * as Layouts from './layouts';
import { ServerChooser } from './components';
// ---Globals
export const BootLoader = ({ vital = 'XBL', step = 0 })=>{
    setCustomVital(vital);
    return /*#__PURE__*/ _jsx(ViewLoader, {
        active: !!step,
        children: /*#__PURE__*/ _jsx("span", {
            style: {
                letterSpacing: '-2px'
            },
            children: '.'.repeat(step)
        })
    });
};
export const AppLoader = connect('AppLoader', 'debug.numActions', ({ numActions })=>{
    let [start] = useState(numActions);
    return /*#__PURE__*/ _jsx(BootLoader, {
        vital: "XAL",
        step: Math.max(numActions - start, 0)
    });
});
let ErrorMessage = ({ header, errors })=>/*#__PURE__*/ _jsxs(Message, {
        negative: true,
        children: [
            /*#__PURE__*/ _jsx(Message.Header, {
                children: header
            }),
            errors.map((e, i)=>/*#__PURE__*/ _jsx("p", {
                    children: e
                }, i))
        ]
    });
class _InitAppError extends Component {
    render() {
        let { deploy, apiurl: url, errors } = this.props;
        let errorLines = isArray(errors) ? errors.map((e)=>`${e.message} [${e.code}] on ${e.url}`) : [];
        if (deploy.mode == 'npm') {
            return /*#__PURE__*/ _jsx(ServerChooser, {
                message: `API Init Error on: ${url}`,
                errors: errorLines
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Title, {
                    children: "VL - Initialization Failure"
                }),
                /*#__PURE__*/ _jsx(ErrorMessage, {
                    header: "Initialization Failure",
                    errors: errorLines
                })
            ]
        });
    }
}
_define_property(_InitAppError, "storeMap", [
    'debug.errors',
    'config.deploy',
    'config.apiurl'
]);
const InitAppError = connect('InitAppError', _InitAppError);
export const ViewNotFound = connect('ViewNotFound', 'meta.router404', ({ router404, text })=>{
    let errors = [];
    if (text) {
        errors.push(text);
    }
    if (router404) {
        errors.push(`${router404.pathname} not found`);
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Title, {
                children: "VL - 404"
            }),
            /*#__PURE__*/ _jsx(ErrorMessage, {
                header: "404",
                errors: errors
            })
        ]
    });
});
let ErrorLines = ({ errors })=>{
    return errors.map((e, i)=>/*#__PURE__*/ _jsx("div", {
            style: {
                fontSize: '1.0em'
            },
            children: /*#__PURE__*/ _jsxs("tt", {
                children: [
                    e.code,
                    ": ",
                    e.message
                ]
            })
        }, i));
};
let ErrorModal = connect('ErrorModal', [
    'router.pathname',
    'debug.errors'
], ({ pathname, errors })=>/*#__PURE__*/ _jsxs(Modal, {
        size: "tiny",
        open: true,
        dimmer: true,
        children: [
            /*#__PURE__*/ _jsx(Header, {
                as: "h3",
                icon: {
                    name: 'bug',
                    color: 'red'
                },
                content: "Fatal Error"
            }),
            /*#__PURE__*/ _jsxs(Modal.Content, {
                children: [
                    "App terminated, restart needed",
                    /*#__PURE__*/ _jsx(ErrorLines, {
                        errors: errors
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Modal.Actions, {
                children: /*#__PURE__*/ _jsx(Button, {
                    basic: true,
                    color: "red",
                    as: "a",
                    href: `${pathname}`,
                    icon: "remove",
                    content: "Restart"
                })
            })
        ]
    }));
let OfflineModal = ({})=>{
    let [ignore, setIgnore] = useState(false);
    if (ignore) return null;
    return /*#__PURE__*/ _jsxs(Modal, {
        size: "tiny",
        open: true,
        dimmer: true,
        children: [
            /*#__PURE__*/ _jsx(Header, {
                as: "h3",
                icon: {
                    name: 'wifi',
                    color: 'red'
                },
                content: "Connection lost"
            }),
            /*#__PURE__*/ _jsx(Modal.Content, {
                style: {
                    height: '20%'
                },
                children: /*#__PURE__*/ _jsx(Loader, {
                    style: {
                        color: 'var(--vl_body_color)'
                    },
                    inline: true,
                    indeterminate: true,
                    children: "Waiting for reconnect..."
                })
            }),
            /*#__PURE__*/ _jsx(Modal.Actions, {
                children: /*#__PURE__*/ _jsx(Button, {
                    basic: true,
                    color: "red",
                    as: "a",
                    icon: "play",
                    content: "Ignore",
                    onClick: ()=>setIgnore(true)
                })
            })
        ]
    });
};
let UnauthorizedModal = ({})=>{
    let i18n = useSelector(Sel.i18n);
    return /*#__PURE__*/ _jsx(Modal, {
        size: "mini",
        basic: true,
        open: true,
        children: /*#__PURE__*/ _jsxs(Message, {
            icon: true,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "lock"
                }),
                /*#__PURE__*/ _jsx(Message.Content, {
                    children: i18n.span('msg_insufficient_permission')
                })
            ]
        })
    });
};
let LoginModal = ({ classcfg, tabcfg })=>{
    /* visible if appstate VIEW_UNAUTHORIZED
   */ let setAppState = useActions(Actions.setAppState);
    if (classcfg.noReloginDialog || tabcfg.noReloginDialog) {
        return /*#__PURE__*/ _jsx(UnauthorizedModal, {});
    }
    return /*#__PURE__*/ _jsx(Modal, {
        basic: true,
        open: true,
        dimmer: "blurring",
        children: /*#__PURE__*/ _jsx(StandalonePart, {
            type: "AuthLogin"
        })
    });
};
class View extends Component {
    componentDidUpdate(prevProps) {
        if (this.props.theme !== prevProps.theme) {
            applyTheme(this.props.theme);
        }
    }
    getAutoParts(layout) {
        // XXX: should also come from tcfg
        let parts = [];
        let { progress, pprintOn, debugFooterOn, themeEditorOn, contextHelpOn, logStreamOn, logStreamMode } = this.props;
        parts.push([
            Parts.get('HTMLHead'),
            {
                type: 'HTMLHead'
            }
        ]);
        if (debugFooterOn) {
            parts.push([
                Parts.get('DebugFooter'),
                {
                    type: 'DebugFooter',
                    align: 'bottom',
                    order: 100
                }
            ]);
        }
        if (layout.startsWith('L131')) {
            if (themeEditorOn) {
                parts.push([
                    Parts.get('ThemeBuilder'),
                    {
                        type: 'ThemeBuilder',
                        align: 'right'
                    }
                ]);
            }
            if (contextHelpOn) {
                parts.push([
                    Parts.get('ContextHelp'),
                    {
                        type: 'ContextHelp',
                        align: 'right'
                    }
                ]);
            }
            if (pprintOn) {
                parts.push([
                    Parts.get('PPrintViz'),
                    {
                        type: 'PPrintViz',
                        align: 'right'
                    }
                ]);
            }
            if (logStreamOn) {
                parts.push([
                    Parts.get('LogConsole'),
                    {
                        type: 'LogConsole',
                        align: logStreamMode == 'dialog' ? 'dialog' : 'right'
                    }
                ]);
            }
        }
        return parts;
    }
    getParts(classcfg, tabcfg, layout) {
        let classlevel = [
            ...this.getAutoParts(layout)
        ];
        let tablevel = [];
        if (!classcfg || !tabcfg) {
            log.error('invalid view config');
        } else {
            each([
                [
                    classcfg,
                    classlevel
                ],
                [
                    tabcfg,
                    tablevel
                ]
            ], ([cfg, level])=>{
                each(cfg.parts, (partcfg)=>{
                    let { type, ...pcfg } = partcfg;
                    // first char is uppercased - react component convention
                    type = ucFirst(type);
                    let Part = Parts.get(type);
                    if (!Part) {
                        pcfg.unknownType = type;
                        type = 'UnknownPart';
                    }
                    level.push([
                        Parts.get(type),
                        {
                            type,
                            ...pcfg
                        }
                    ]);
                });
            });
        }
        return {
            classlevel,
            tablevel
        };
    }
    render() {
        const { appstate, classname, tabname, dname, classcfg, runId, theme } = this.props;
        if (appstate == 'INIT_APP_ERROR' || appstate == 'API_SWITCH') {
            return /*#__PURE__*/ _jsx(InitAppError, {});
        }
        if (appstate == 'INIT_APP_404') {
            return /*#__PURE__*/ _jsx(ViewNotFound, {});
        }
        if (runId === 0 || appstate === 'INIT_APP') {
            // waiting for application boot
            return /*#__PURE__*/ _jsx(AppLoader, {});
        }
        if (!classcfg) {
            return /*#__PURE__*/ _jsx(ViewNotFound, {
                text: `tcfg class not found: ${dname}/${classname}`
            });
        }
        if (!tabname) {
            return /*#__PURE__*/ _jsx(ViewNotFound, {
                text: 'no tab selected'
            });
        }
        let tabcfg = classcfg.tabs[tabname];
        if (!tabcfg) {
            return /*#__PURE__*/ _jsx(ViewNotFound, {
                text: `tcfg tab not found: ${dname}/${classname}/${tabname}`
            });
        }
        let errors = null;
        if (appstate == 'FATAL_ERROR') {
            errors = /*#__PURE__*/ _jsx(ErrorModal, {});
        }
        if (appstate == 'API_OFFLINE') {
            errors = /*#__PURE__*/ _jsx(OfflineModal, {});
        }
        if (appstate == 'VIEW_UNAUTHORIZED') {
            errors = /*#__PURE__*/ _jsx(LoginModal, {
                classcfg: classcfg,
                tabcfg: tabcfg
            });
        }
        let layout = get(tabcfg, 'layout', get(classcfg, 'layout', 'L131'));
        let p = [
            dname,
            classname,
            tabname
        ];
        log.info(`rendering: %o [${layout}]`, p.filter((e)=>e).join('/'));
        // first char is uppercased - react component convention
        let Layout = Layouts[ucFirst(layout)];
        if (!Layout) {
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    "NoLayout: ",
                    layout
                ]
            });
        }
        // get part configurations
        let parts = this.getParts(classcfg, tabcfg, layout);
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                errors,
                /*#__PURE__*/ _jsx(DomainStyles, {}),
                /*#__PURE__*/ _jsx(NotificationManager, {}),
                /*#__PURE__*/ _jsx(PortalTarget, {
                    id: "rootPortal"
                }),
                /*#__PURE__*/ _jsx(Layout, {
                    parts: parts,
                    theme: theme
                }),
                /*#__PURE__*/ _jsx(GlobalKeyboardShortcuts, {})
            ]
        });
    }
    constructor(props){
        super(props);
        this.state = {
            loadingPartData: false
        };
    }
}
_define_property(View, "storeMap", (root)=>{
    let request = root.meta.request;
    return {
        // official deps
        appstate: Sel.appstate(root),
        runId: Sel.runId(root),
        dname: request.dname,
        classname: request.classname,
        tabname: request.tabname,
        classcfg: Sel.classCfg(root),
        // some hacks ahead
        pprintOn: !isNil(root.meta.pprint),
        debugFooterOn: Sel.isDebugOn(root),
        themeEditorOn: root.debug.isThemeEditorOn,
        contextHelpOn: root.meta.contextHelp.on,
        theme: root.meta.theme.name,
        logStreamOn: root.meta.logstream.on,
        logStreamMode: root.meta.logstream.mode
    };
});
const v = connect('View', View);
export { v as View };
